import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { Box, Flex, Heading } from '@radix-ui/themes';
import React from 'react';
import { useIsolatedLimecraft } from '../hooks/useIsolatedLimecraft';
import { AdvancedVideoEngineProps } from '../types';
import { createPlayerObserver } from '../utils/playerObserver';
import { InteractionRect, LoadingLayer, ScrubberLayer } from './controls';

import styles from './Limecraft.module.css';

const CONTROL_BAR_HEIGHT = 50;

export const LimecraftEngine = ({
    src,
    media,
    onReady,
    tracks,
}: AdvancedVideoEngineProps) => {
    const currentTimeRef = React.useRef(0);
    const observer = React.useMemo(() => createPlayerObserver(), []);

    const aspectRatio = Math.max(media.width / media.height, 1.3666);

    const { player, iframeRef, calculatedHeight, isLoading, isError } =
        useIsolatedLimecraft(
            'lc-player-instance' as const,
            aspectRatio,
            CONTROL_BAR_HEIGHT,
        );

    React.useEffect(() => {
        if (player) {
            onReady({
                // Play / Pause
                play: () => player.play(),
                pause: () => player.pause(),
                togglePlayPause: () => player.togglePlayPause(),
                // Playback Speed
                faster: () => player.faster(),
                slower: () => player.slower(),
                // Seeking
                seek: (time) => player.seek(time),
                seekRelative: (time) => player.seekRelative(time),
                seekFrames: (frames) => player.seekFrames(frames),
                seekRelativeFrames: (frames) =>
                    player.seekRelativeFrames(frames),
                nextFrame: () => player.nextFrame(),
                previousFrame: () => player.previousFrame(),
                // Playlist
                playlistPrevious: () => player.playlistPrevious(),
                playlistNext: () => player.playlistNext(),
                // Position
                getCurrentTime: () => currentTimeRef.current,
                // Update UI
                subscribe: observer.subscribe,
            });
        }
    }, [observer, onReady, player]);

    React.useEffect(() => {
        if (player && src) {
            player.setPlaylist([
                {
                    sources: [
                        {
                            src,
                            type: media.mimeType,
                            'supported-players': 'remote,local',
                        },
                    ],
                    frameRate: media.frameRate.fps,
                },
            ]);
        }
    }, [player, src, media.mimeType, media.frameRate.fps]);

    const handleSeek = React.useCallback(
        (frame: number) => {
            if (!player) return;
            player.seekFrames(frame);
        },
        [player],
    );

    const zone: InteractionRect = React.useMemo(
        () => ({
            width: '100%',
            height: '20px',
            bottom: CONTROL_BAR_HEIGHT - 10,
            left: 0,
            right: 0,
            position: 'absolute',
        }),
        [],
    );

    return (
        <Box
            className={styles.engineWrapper}
            style={{
                height: calculatedHeight > 0 ? `${calculatedHeight}px` : 'auto',
                /* This is only to avoid empty gap under Limecraft player iframe
                as it can't handle some exotic aspect ratio properly
                and seems to have an internal max-height set to 750px */
                // maxHeight: '750px',
                // aspectRatio: `${Math.max(aspectRatio, 1.3666)}`,
            }}
        >
            {/* Loading Layer */}
            <LoadingLayer isLoading={isLoading} style={{ aspectRatio }} />

            {/* Media Error Layer */}
            {isError && (
                <Flex
                    className={styles.mediaToggleLayer}
                    gap={'3'}
                    align={'center'}
                    justify={'center'}
                    asChild
                >
                    <Heading color='amber' weight={'bold'}>
                        <ExclamationTriangleIcon
                            height={'32px'}
                            width={'32px'}
                        />
                        Media unavailable
                    </Heading>
                </Flex>
            )}

            {/* Media Content Layer */}
            {!isError && (
                <iframe ref={iframeRef} className={styles.playerInstance} />
            )}

            {!isLoading && !isError && (
                <>
                    {/* Media toggle layer */}
                    <Box
                        className={styles.mediaToggleLayer}
                        style={{ bottom: `${CONTROL_BAR_HEIGHT}px` }}
                        onClick={() => player?.togglePlayPause()}
                    />

                    {/* Preview layer */}
                    <ScrubberLayer
                        totalFrames={media.frames}
                        tracks={tracks}
                        frameRate={media.frameRate}
                        onSeek={handleSeek}
                        zone={zone}
                        previewOffset={20}
                        aspectRatio={aspectRatio}
                    />
                </>
            )}
        </Box>
    );
};
